/**
 * Service Types tab (Settings) – CRUD for service types and their fields/options.
 * Types and fields support sort order; options can be configured for select fields.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Settings as OptionsIcon,
} from '@mui/icons-material';
import ApiService from '../services/api';
import { isDomainNameServiceType } from '../utils/domainName';

const SETTINGS_API_BASE = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

interface ServiceTypeItem {
  id: string;
  code: string;
  name: string;
  sort_order: number;
  logo_path?: string | null;
  updated_at?: string;
}

interface ServiceTypeFieldItem {
  id: string;
  service_type_id: string;
  field_key: string;
  label: string;
  field_type: string;
  required: boolean;
  sort_order: number;
  options?: { id: string; value: string; label: string; sort_order: number }[];
  conditions?: Record<string, unknown> | null;
}

interface ServiceTypesTabProps {
  onError?: (msg: string) => void;
}

const FIELD_TYPES = ['text', 'number', 'select', 'textarea', 'date', 'password', 'account_service'];

const ServiceTypesTab: React.FC<ServiceTypesTabProps> = ({ onError }) => {
  const [types, setTypes] = useState<ServiceTypeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<ServiceTypeItem | null>(null);
  const [fields, setFields] = useState<ServiceTypeFieldItem[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);

  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [typeEditing, setTypeEditing] = useState<ServiceTypeItem | null>(null);
  const [typeForm, setTypeForm] = useState({ code: '', name: '', sort_order: 0 });
  const [typeSaving, setTypeSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreviewRevision, setLogoPreviewRevision] = useState(0);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [fieldEditing, setFieldEditing] = useState<ServiceTypeFieldItem | null>(null);
  const [fieldForm, setFieldForm] = useState({ field_key: '', label: '', field_type: 'text', required: false, sort_order: 0 });
  const [fieldSaving, setFieldSaving] = useState(false);

  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
  const [optionsField, setOptionsField] = useState<ServiceTypeFieldItem | null>(null);
  const [options, setOptions] = useState<{ id: string; value: string; label: string; sort_order: number }[]>([]);
  const [optionForm, setOptionForm] = useState({ value: '', label: '' });
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [optionEditingId, setOptionEditingId] = useState<string | null>(null);
  const [optionSaving, setOptionSaving] = useState(false);

  const showError = (msg: string) => {
    setError(msg);
    onError?.(msg);
  };

  const loadTypes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ApiService.getServiceTypes(false);
      setTypes(Array.isArray(data) ? data : []);
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to load service types');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFields = useCallback(async (typeId: string) => {
    try {
      setFieldsLoading(true);
      const data = await ApiService.getServiceType(typeId);
      setFields(data?.fields ?? []);
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to load fields');
    } finally {
      setFieldsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  useEffect(() => {
    if (selectedType) loadFields(selectedType.id);
    else setFields([]);
  }, [selectedType, loadFields]);

  const handleSelectType = (t: ServiceTypeItem) => setSelectedType(prev => (prev?.id === t.id ? null : t));

  const handleOpenAddType = () => {
    setTypeEditing(null);
    setTypeForm({ code: '', name: '', sort_order: types.length ? Math.max(...types.map(x => x.sort_order), 0) + 10 : 0 });
    setTypeDialogOpen(true);
  };

  const handleOpenEditType = (t: ServiceTypeItem) => {
    const fresh = types.find((x) => x.id === t.id) ?? t;
    setTypeEditing({ ...fresh });
    setTypeForm({ code: fresh.code, name: fresh.name, sort_order: fresh.sort_order });
    setLogoPreviewRevision((n) => n + 1);
    setTypeDialogOpen(true);
  };

  const handleSaveType = async () => {
    const code = typeForm.code.trim().toLowerCase();
    const name = typeForm.name.trim();
    if (!code || !name) {
      showError('Code and name are required');
      return;
    }
    setTypeSaving(true);
    setError(null);
    try {
      if (typeEditing) {
        await ApiService.updateServiceType(typeEditing.id, { code, name, sort_order: typeForm.sort_order });
      } else {
        await ApiService.createServiceType({ code, name, sort_order: typeForm.sort_order });
      }
      setTypeDialogOpen(false);
      await loadTypes();
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to save service type');
    } finally {
      setTypeSaving(false);
    }
  };

  const serviceTypeLogoPublicUrl = (code: string, cacheBust?: number | string) => {
    const base = `${SETTINGS_API_BASE.replace(/\/$/, '')}/api/orders/service-type-logos/${encodeURIComponent(code)}`;
    if (cacheBust === undefined || cacheBust === '') return base;
    return `${base}?v=${encodeURIComponent(String(cacheBust))}`;
  };

  const handleServiceTypeLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !typeEditing) return;
    const editingId = typeEditing.id;
    setLogoUploading(true);
    setError(null);
    try {
      const res = await ApiService.uploadServiceTypeLogo(editingId, file);
      const data = await ApiService.getServiceTypes(false);
      const list = Array.isArray(data) ? data : [];
      setTypes(list);
      setLogoPreviewRevision((n) => n + 1);
      setTypeEditing((prev) => {
        if (!prev || prev.id !== editingId) return prev;
        const row = list.find((x: ServiceTypeItem) => x.id === editingId);
        return row
          ? { ...row }
          : {
              ...prev,
              logo_path: res.logo_path ?? prev.logo_path,
              updated_at: res.updated_at ?? prev.updated_at,
            };
      });
      if (selectedType?.id === editingId) {
        const row = list.find((x: ServiceTypeItem) => x.id === editingId);
        if (row) setSelectedType(row);
      }
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveServiceTypeLogo = async () => {
    if (!typeEditing?.logo_path) return;
    const editingId = typeEditing.id;
    if (!window.confirm('Remove this service type logo?')) return;
    setLogoUploading(true);
    setError(null);
    try {
      await ApiService.deleteServiceTypeLogo(editingId);
      const data = await ApiService.getServiceTypes(false);
      const list = Array.isArray(data) ? data : [];
      setTypes(list);
      setLogoPreviewRevision((n) => n + 1);
      setTypeEditing((prev) => {
        if (!prev || prev.id !== editingId) return prev;
        const row = list.find((x: ServiceTypeItem) => x.id === editingId);
        return row ? { ...row } : { ...prev, logo_path: null };
      });
      if (selectedType?.id === editingId) {
        const row = list.find((x: ServiceTypeItem) => x.id === editingId);
        if (row) setSelectedType(row);
      }
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'Failed to remove logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleDeleteType = async (t: ServiceTypeItem) => {
    if (!window.confirm(`Delete service type "${t.name}" (${t.code})? This will fail if any account services use this type.`)) return;
    try {
      await ApiService.deleteServiceType(t.id);
      if (selectedType?.id === t.id) setSelectedType(null);
      await loadTypes();
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleMoveType = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= types.length) return;
    const reordered = [...types];
    const [removed] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, removed);
    const payload = reordered.map((t, i) => ({ id: t.id, sort_order: i * 10 }));
    try {
      const data = await ApiService.reorderServiceTypes(payload);
      setTypes(Array.isArray(data) ? data : reordered.map((t, i) => ({ ...t, sort_order: i * 10 })));
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to reorder');
    }
  };

  const handleOpenAddField = () => {
    setFieldEditing(null);
    setFieldForm({
      field_key: '',
      label: '',
      field_type: 'text',
      required: false,
      sort_order: fields.length ? Math.max(0, ...fields.map(f => f.sort_order)) + 10 : 0,
    });
    setFieldDialogOpen(true);
  };

  const handleOpenEditField = (f: ServiceTypeFieldItem) => {
    setFieldEditing(f);
    setFieldForm({
      field_key: f.field_key,
      label: f.label,
      field_type: f.field_type,
      required: f.required,
      sort_order: f.sort_order,
    });
    setFieldDialogOpen(true);
  };

  const handleSaveField = async () => {
    if (!selectedType) return;
    const field_key = fieldForm.field_key.trim();
    const label = fieldForm.label.trim();
    if (!field_key || !label) {
      showError('Field key and label are required');
      return;
    }
    setFieldSaving(true);
    setError(null);
    try {
      if (fieldEditing) {
        await ApiService.updateServiceTypeField(selectedType.id, fieldEditing.id, {
          field_key,
          label,
          field_type: fieldForm.field_type,
          required: fieldForm.required,
          sort_order: fieldForm.sort_order,
        });
      } else {
        await ApiService.createServiceTypeField(selectedType.id, {
          field_key,
          label,
          field_type: fieldForm.field_type,
          required: fieldForm.required,
          sort_order: fieldForm.sort_order,
        });
      }
      setFieldDialogOpen(false);
      await loadFields(selectedType.id);
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to save field');
    } finally {
      setFieldSaving(false);
    }
  };

  const handleDeleteField = async (f: ServiceTypeFieldItem) => {
    if (!selectedType) return;
    if (!window.confirm(`Delete field "${f.label}"?`)) return;
    try {
      await ApiService.deleteServiceTypeField(selectedType.id, f.id);
      await loadFields(selectedType.id);
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to delete field');
    }
  };

  const handleMoveField = async (index: number, direction: 'up' | 'down') => {
    if (!selectedType) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;
    const reordered = [...fields];
    const [removed] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, removed);
    const payload = reordered.map((f, i) => ({ id: f.id, sort_order: i * 10 }));
    try {
      await ApiService.reorderServiceTypeFields(selectedType.id, payload);
      await loadFields(selectedType.id);
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to reorder fields');
    }
  };

  const handleOpenOptions = (f: ServiceTypeFieldItem) => {
    setOptionsField(f);
    setOptions(f.options ?? []);
    setOptionForm({ value: '', label: '' });
    setOptionEditingId(null);
    setOptionsDialogOpen(true);
  };

  const loadOptionsForField = async () => {
    if (!selectedType || !optionsField) return;
    try {
      const data = await ApiService.getFieldOptions(selectedType.id, optionsField.id);
      setOptions(Array.isArray(data) ? data : []);
    } catch (_) {
      setOptions([]);
    }
  };

  useEffect(() => {
    if (optionsDialogOpen && optionsField && selectedType) loadOptionsForField();
  }, [optionsDialogOpen, optionsField?.id, selectedType?.id]);

  const handleAddOption = () => {
    setOptionEditingId(null);
    setOptionForm({ value: '', label: '' });
    setOptionDialogOpen(true);
  };

  const handleEditOption = (opt: { id: string; value: string; label: string }) => {
    setOptionEditingId(opt.id);
    setOptionForm({ value: opt.value, label: opt.label || '' });
    setOptionDialogOpen(true);
  };

  const handleSaveOption = async () => {
    if (!selectedType || !optionsField) return;
    const value = optionForm.value.trim();
    if (!value) {
      showError('Value is required');
      return;
    }
    setOptionSaving(true);
    try {
      if (optionEditingId) {
        await ApiService.updateFieldOption(selectedType.id, optionsField.id, optionEditingId, {
          value,
          label: optionForm.label.trim() || undefined,
        });
      } else {
        await ApiService.createFieldOption(selectedType.id, optionsField.id, {
          value,
          label: optionForm.label.trim() || undefined,
          sort_order: options.length * 10,
        });
      }
      setOptionDialogOpen(false);
      await loadOptionsForField();
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to save option');
    } finally {
      setOptionSaving(false);
    }
  };

  const handleDeleteOption = async (optionId: string) => {
    if (!selectedType || !optionsField) return;
    if (!window.confirm('Delete this option?')) return;
    try {
      await ApiService.deleteFieldOption(selectedType.id, optionsField.id, optionId);
      await loadOptionsForField();
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to delete option');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ flex: '1 1 400px', minWidth: 0, p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Service Types</Typography>
            <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleOpenAddType}>
              Add type
            </Button>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Logo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {types.map((t, idx) => (
                    <TableRow
                      key={t.id}
                      hover
                      selected={selectedType?.id === t.id}
                      onClick={() => handleSelectType(t)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMoveType(idx, 'up'); }} disabled={idx === 0}><ArrowUpIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMoveType(idx, 'down'); }} disabled={idx === types.length - 1}><ArrowDownIcon fontSize="small" /></IconButton>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, width: 88 }} onClick={(e) => e.stopPropagation()}>
                        {t.logo_path ? (
                          <Box
                            component="img"
                            src={serviceTypeLogoPublicUrl(t.code, t.updated_at || t.logo_path)}
                            alt=""
                            sx={{ maxHeight: 36, maxWidth: 72, objectFit: 'contain', display: 'block' }}
                            onError={(ev) => {
                              (ev.target as HTMLImageElement).style.visibility = 'hidden';
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>{t.code}</TableCell>
                      <TableCell>{t.name}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" onClick={() => handleOpenEditType(t)} title="Edit type"><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => handleDeleteType(t)} title="Delete type"><DeleteIcon fontSize="small" color="error" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!loading && types.length === 0 && (
            <Typography color="text.secondary" textAlign="center" py={2}>No service types. Add one to get started.</Typography>
          )}
        </Paper>

        <Paper sx={{ flex: '1 1 400px', minWidth: 0, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {selectedType ? `Fields: ${selectedType.name}` : 'Select a type'}
          </Typography>
          {selectedType && (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={handleOpenAddField}>
                  Add field
                </Button>
                {isDomainNameServiceType(selectedType.code, selectedType.name) && (
                  <Button
                    component={RouterLink}
                    to="/admin/domains/import"
                    variant="contained"
                    size="small"
                  >
                    Import from Synergy
                  </Button>
                )}
              </Box>
              {fieldsLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Label</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Required</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((f, idx) => (
                        <TableRow key={f.id}>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleMoveField(idx, 'up')} disabled={idx === 0}><ArrowUpIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => handleMoveField(idx, 'down')} disabled={idx === fields.length - 1}><ArrowDownIcon fontSize="small" /></IconButton>
                          </TableCell>
                          <TableCell>{f.field_key}</TableCell>
                          <TableCell>{f.label}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip size="small" label={f.field_type} />
                              {f.conditions && <Chip size="small" label="Conditional" color="info" variant="outlined" />}
                              {f.field_type === 'select' && (
                                <IconButton size="small" onClick={() => handleOpenOptions(f)} title="Edit options"><OptionsIcon fontSize="small" /></IconButton>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{f.required ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleOpenEditField(f)}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => handleDeleteField(f)}><DeleteIcon fontSize="small" color="error" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!fieldsLoading && fields.length === 0 && (
                <Typography color="text.secondary" variant="body2">No fields. Add fields to define what appears for this service type.</Typography>
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* Type dialog */}
      <Dialog open={typeDialogOpen} onClose={() => setTypeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{typeEditing ? 'Edit service type' : 'Add service type'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Code"
            fullWidth
            value={typeForm.code}
            onChange={(e) => setTypeForm(prev => ({ ...prev, code: e.target.value }))}
            placeholder="e.g. nbn, sonweb"
            disabled={!!typeEditing}
            helperText={typeEditing ? 'Code cannot be changed' : 'Lowercase, no spaces'}
          />
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={typeForm.name}
            onChange={(e) => setTypeForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g. NBN, Sonweb"
          />
          <TextField
            margin="dense"
            label="Sort order"
            type="number"
            fullWidth
            value={typeForm.sort_order}
            onChange={(e) => setTypeForm(prev => ({ ...prev, sort_order: parseInt(e.target.value, 10) || 0 }))}
          />
          {typeEditing ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Service logo
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Used on the public order review when this type&apos;s <strong>code</strong> matches the order
                subcategory slug (e.g. code <code>nbn</code> with subcategory &quot;nbn&quot;). PNG, JPG, WebP, or GIF;
                max 2&nbsp;MB.
              </Typography>
              <input
                ref={logoFileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                onChange={handleServiceTypeLogoFile}
              />
              <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Current logo (as stored)
                </Typography>
                {typeEditing.logo_path ? (
                  <Box
                    component="img"
                    key={`${typeEditing.id}-${typeEditing.logo_path}-${logoPreviewRevision}`}
                    src={serviceTypeLogoPublicUrl(typeEditing.code, logoPreviewRevision)}
                    alt=""
                    sx={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain', display: 'block' }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No logo on file. Upload an image below.
                  </Typography>
                )}
              </Paper>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={logoUploading}
                  onClick={() => logoFileInputRef.current?.click()}
                >
                  {logoUploading ? 'Uploading…' : typeEditing.logo_path ? 'Replace logo' : 'Upload logo'}
                </Button>
                {typeEditing.logo_path ? (
                  <Button size="small" color="error" variant="outlined" disabled={logoUploading} onClick={handleRemoveServiceTypeLogo}>
                    Remove logo
                  </Button>
                ) : null}
              </Box>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Save the new service type first, then use <strong>Edit</strong> to upload a logo.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveType} variant="contained" disabled={typeSaving}>{typeSaving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Field dialog */}
      <Dialog open={fieldDialogOpen} onClose={() => setFieldDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{fieldEditing ? 'Edit field' : 'Add field'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Field key"
            fullWidth
            value={fieldForm.field_key}
            onChange={(e) => setFieldForm(prev => ({ ...prev, field_key: e.target.value }))}
            placeholder="e.g. speed_plan, pop"
            disabled={!!fieldEditing}
            helperText={fieldEditing ? 'Key cannot be changed' : 'Stored in type_specific_data'}
          />
          <TextField
            margin="dense"
            label="Label"
            fullWidth
            value={fieldForm.label}
            onChange={(e) => setFieldForm(prev => ({ ...prev, label: e.target.value }))}
            placeholder="e.g. NBN AVC"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Field type</InputLabel>
            <Select
              value={fieldForm.field_type}
              label="Field type"
              onChange={(e) => setFieldForm(prev => ({ ...prev, field_type: e.target.value }))}
            >
              {FIELD_TYPES.map(ft => (<MenuItem key={ft} value={ft}>{ft}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Required</InputLabel>
            <Select
              value={fieldForm.required ? 'yes' : 'no'}
              label="Required"
              onChange={(e) => setFieldForm(prev => ({ ...prev, required: e.target.value === 'yes' }))}
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Sort order"
            type="number"
            fullWidth
            value={fieldForm.sort_order}
            onChange={(e) => setFieldForm(prev => ({ ...prev, sort_order: parseInt(e.target.value, 10) || 0 }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveField} variant="contained" disabled={fieldSaving}>{fieldSaving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Options dialog (for select fields) */}
      <Dialog open={optionsDialogOpen} onClose={() => setOptionsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Options: {optionsField?.label}</DialogTitle>
        <DialogContent>
          <Button startIcon={<AddIcon />} size="small" onClick={handleAddOption} sx={{ mb: 2 }}>Add option</Button>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Value</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {options.map(opt => (
                  <TableRow key={opt.id}>
                    <TableCell>{opt.value}</TableCell>
                    <TableCell>{opt.label || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEditOption(opt)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDeleteOption(opt.id)}><DeleteIcon fontSize="small" color="error" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Single option add/edit dialog */}
      <Dialog open={optionDialogOpen} onClose={() => setOptionDialogOpen(false)}>
        <DialogTitle>{optionEditingId ? 'Edit option' : 'Add option'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Value"
            fullWidth
            value={optionForm.value}
            onChange={(e) => setOptionForm(prev => ({ ...prev, value: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Label (optional)"
            fullWidth
            value={optionForm.label}
            onChange={(e) => setOptionForm(prev => ({ ...prev, label: e.target.value }))}
            placeholder="Display text; defaults to value"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveOption} variant="contained" disabled={optionSaving}>{optionSaving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceTypesTab;
